import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  BookOpenIcon, PlusIcon, PencilSquareIcon, TrashIcon, DocumentTextIcon,
  ChevronRightIcon, XMarkIcon, MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import EditorHtml from '../components/kb/EditorHtml';

const isAdminUser = () => {
  try { const u = JSON.parse(localStorage.getItem('user')); return String(u?.role || u?.ruolo || '').toLowerCase() === 'admin'; }
  catch (e) { return false; }
};

const formatData = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch (e) { return ''; }
};

const inputCls = 'w-full px-4 py-2.5 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400';
const labelCls = 'block text-xs font-medium text-gray-500 mb-1.5';
const cardCls = 'bg-white/80 backdrop-blur-xl shadow-lg shadow-gray-200/20 rounded-2xl border border-gray-100/50';

export default function GuidaPage() {
  const isAdmin = isAdminUser();
  const [sezioni, setSezioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [articoloSel, setArticoloSel] = useState(null);
  const [editing, setEditing] = useState(null);
  const [modalSezione, setModalSezione] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [cerca, setCerca] = useState('');

  const carica = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/kb/sezioni'); setSezioni(Array.isArray(data) ? data : []); }
    catch (e) { toast.error('Non riesco a caricare la guida. Riprova.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const apriArticolo = async (id) => {
    setEditing(null);
    try { const { data } = await api.get(`/kb/articoli/${id}`); setArticoloSel(data); }
    catch (e) { toast.error('Articolo non disponibile'); }
  };

  // ---- Articoli (admin) ----
  const nuovoArticolo = (idSezione) => { setArticoloSel(null); setEditing({ idSezione, titolo: '', contenutoHtml: '', pubblicato: true }); };
  const modificaArticolo = () => articoloSel && setEditing({ ...articoloSel });
  const salvaArticolo = async () => {
    if (!editing.titolo.trim()) { toast.warning('Dai un titolo all\'articolo'); return; }
    setSalvando(true);
    try {
      let saved;
      if (editing.id) saved = (await api.put(`/kb/articoli/${editing.id}`, editing)).data;
      else saved = (await api.post('/kb/articoli', editing)).data;
      toast.success('Articolo salvato');
      setEditing(null);
      await carica();
      apriArticolo(saved.id);
    } catch (err) { toast.error(err.response?.data?.error || 'Non sono riuscito a salvare'); }
    finally { setSalvando(false); }
  };
  const eliminaArticolo = async (art) => {
    if (!window.confirm(`Eliminare l'articolo "${art.titolo}"?`)) return;
    try { await api.delete(`/kb/articoli/${art.id}`); toast.success('Articolo eliminato'); setArticoloSel(null); setEditing(null); carica(); }
    catch (e) { toast.error('Non sono riuscito a eliminare'); }
  };

  // ---- Sezioni (admin) ----
  const salvaSezione = async () => {
    if (!modalSezione.titolo.trim()) { toast.warning('Dai un titolo alla sezione'); return; }
    setSalvando(true);
    try {
      if (modalSezione.id) await api.put(`/kb/sezioni/${modalSezione.id}`, modalSezione);
      else await api.post('/kb/sezioni', modalSezione);
      toast.success('Sezione salvata');
      setModalSezione(null);
      carica();
    } catch (err) { toast.error(err.response?.data?.error || 'Non sono riuscito a salvare'); }
    finally { setSalvando(false); }
  };
  const eliminaSezione = async (sez) => {
    if (!window.confirm(`Eliminare la sezione "${sez.titolo}" e tutti i suoi articoli?`)) return;
    try { await api.delete(`/kb/sezioni/${sez.id}`); toast.success('Sezione eliminata'); if (articoloSel?.idSezione === sez.id) setArticoloSel(null); carica(); }
    catch (e) { toast.error('Non sono riuscito a eliminare'); }
  };

  // filtro ricerca
  const q = cerca.trim().toLowerCase();
  const sezioniFiltrate = sezioni.map(s => ({
    ...s,
    articoli: (s.articoli || []).filter(a => !q || a.titolo.toLowerCase().includes(q) || s.titolo.toLowerCase().includes(q))
  })).filter(s => !q || s.articoli.length || s.titolo.toLowerCase().includes(q));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50/50 via-white to-blue-50/30">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 space-y-6">

        {/* Header */}
        <div className={`${cardCls} p-6`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-500/25 flex-shrink-0">
                <BookOpenIcon className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Guida</h1>
                <p className="text-gray-500 mt-1">Procedure, istruzioni e video per usare la piattaforma.</p>
              </div>
            </div>
            {isAdmin && (
              <button onClick={() => setModalSezione({ titolo: '', descrizione: '', icona: '', ordine: sezioni.length })}
                className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-medium shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transform hover:scale-105 transition-all duration-200">
                <PlusIcon className="h-5 w-5" /> Nuova sezione
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">

          {/* Indice */}
          <aside className={`${cardCls} p-4 h-fit lg:sticky lg:top-6`}>
            <div className="relative mb-4">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input value={cerca} onChange={e => setCerca(e.target.value)} placeholder="Cerca…"
                className="w-full pl-10 pr-3 py-2.5 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all placeholder-gray-400" />
            </div>

            {loading ? (
              <p className="text-sm text-gray-400 px-2 py-4">Caricamento…</p>
            ) : sezioniFiltrate.length === 0 ? (
              <p className="text-sm text-gray-400 px-2 py-4">{cerca ? 'Nessun risultato.' : (isAdmin ? 'Crea la prima sezione per iniziare.' : 'Nessun contenuto disponibile.')}</p>
            ) : (
              <div className="space-y-3">
                {sezioniFiltrate.map(sez => (
                  <div key={sez.id}>
                    <div className="flex items-center gap-1 px-2 py-1 group">
                      <span className="text-sm font-semibold text-gray-800 flex-1 truncate">{sez.icona ? `${sez.icona} ` : ''}{sez.titolo}</span>
                      {isAdmin && (
                        <span className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <button onClick={() => nuovoArticolo(sez.id)} title="Nuovo articolo" className="p-1 text-gray-400 hover:text-blue-600"><PlusIcon className="h-4 w-4" /></button>
                          <button onClick={() => setModalSezione({ ...sez })} title="Modifica sezione" className="p-1 text-gray-400 hover:text-gray-700"><PencilSquareIcon className="h-4 w-4" /></button>
                          <button onClick={() => eliminaSezione(sez)} title="Elimina sezione" className="p-1 text-gray-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                        </span>
                      )}
                    </div>
                    <div className="space-y-0.5 mt-0.5">
                      {(sez.articoli || []).map(a => {
                        const attivo = articoloSel?.id === a.id && !editing;
                        return (
                          <button key={a.id} onClick={() => apriArticolo(a.id)}
                            className={`w-full text-left flex items-center gap-1.5 pl-3 pr-2 py-2 rounded-xl text-sm transition-colors ${attivo ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
                            <ChevronRightIcon className={`h-3.5 w-3.5 flex-shrink-0 transition-transform ${attivo ? 'text-blue-500 translate-x-0.5' : 'opacity-40'}`} />
                            <span className="truncate flex-1">{a.titolo}</span>
                            {isAdmin && !a.pubblicato && <span className="text-[9px] uppercase tracking-wide bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded flex-shrink-0">bozza</span>}
                          </button>
                        );
                      })}
                      {isAdmin && (sez.articoli || []).length === 0 && (
                        <button onClick={() => nuovoArticolo(sez.id)} className="w-full text-left pl-3 py-1.5 text-xs text-gray-400 hover:text-blue-600 transition-colors">+ aggiungi articolo</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </aside>

          {/* Contenuto */}
          <main className="min-w-0">
            {editing ? (
              <div className={`${cardCls} p-6 md:p-8`}>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-xl font-bold text-gray-900">{editing.id ? 'Modifica articolo' : 'Nuovo articolo'}</h2>
                  <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-700 transition-colors"><XMarkIcon className="h-5 w-5" /></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-5">
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Titolo</label>
                    <input value={editing.titolo} onChange={e => setEditing({ ...editing, titolo: e.target.value })} className={inputCls} placeholder="Es. Come si crea una pratica" />
                  </div>
                  <div>
                    <label className={labelCls}>Sezione</label>
                    <select value={editing.idSezione} onChange={e => setEditing({ ...editing, idSezione: Number(e.target.value) })} className={inputCls}>
                      {sezioni.map(s => <option key={s.id} value={s.id}>{s.titolo}</option>)}
                    </select>
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input type="checkbox" checked={!!editing.pubblicato} onChange={e => setEditing({ ...editing, pubblicato: e.target.checked })} className="accent-blue-600 h-4 w-4" />
                      Pubblicato (visibile a tutti)
                    </label>
                  </div>
                </div>
                <label className={labelCls}>Contenuto</label>
                <EditorHtml value={editing.contenutoHtml} onChange={(html) => setEditing(prev => ({ ...prev, contenutoHtml: html }))} />
                <div className="flex justify-between items-center mt-6 pt-5 border-t border-gray-100">
                  {editing.id
                    ? <button onClick={() => eliminaArticolo(editing)} className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1.5"><TrashIcon className="h-4 w-4" /> Elimina</button>
                    : <span />}
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Annulla</button>
                    <button onClick={salvaArticolo} disabled={salvando} className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl text-sm font-medium shadow-lg shadow-blue-500/25 hover:shadow-xl disabled:opacity-50 transition-all">{salvando ? 'Salvataggio…' : 'Salva articolo'}</button>
                  </div>
                </div>
              </div>
            ) : articoloSel ? (
              <article className={`${cardCls} p-6 md:p-9`}>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold pt-1">
                    {articoloSel.sezione?.titolo}
                    {articoloSel.updatedAt && <span className="text-gray-400 font-normal normal-case tracking-normal"> · aggiornato il {formatData(articoloSel.updatedAt)}</span>}
                  </p>
                  {isAdmin && (
                    <button onClick={modificaArticolo} className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-blue-300 hover:text-blue-700 transition-colors flex-shrink-0">
                      <PencilSquareIcon className="h-4 w-4" /> Modifica
                    </button>
                  )}
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{articoloSel.titolo}</h1>
                <div className="kb-content" dangerouslySetInnerHTML={{ __html: articoloSel.contenutoHtml || '<p style="color:#9ca3af">Questo articolo non ha ancora contenuto.</p>' }} />
              </article>
            ) : (
              <div className={`${cardCls} px-8 py-16 md:py-24 text-center`}>
                <div className="h-16 w-16 rounded-2xl bg-blue-50 text-blue-500 flex items-center justify-center mx-auto mb-5">
                  <DocumentTextIcon className="h-8 w-8" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Seleziona un argomento</h2>
                <p className="text-gray-500 mt-2 max-w-sm mx-auto">Scegli una voce dall'indice qui a fianco per aprire la guida.</p>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Modale sezione */}
      {modalSezione && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setModalSezione(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-5">{modalSezione.id ? 'Modifica sezione' : 'Nuova sezione'}</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-20">
                  <label className={labelCls}>Icona</label>
                  <input value={modalSezione.icona || ''} onChange={e => setModalSezione({ ...modalSezione, icona: e.target.value })} placeholder="📘" className={`${inputCls} text-center text-lg`} />
                </div>
                <div className="flex-1">
                  <label className={labelCls}>Titolo</label>
                  <input value={modalSezione.titolo} onChange={e => setModalSezione({ ...modalSezione, titolo: e.target.value })} className={inputCls} placeholder="Es. Pratiche" autoFocus />
                </div>
              </div>
              <div>
                <label className={labelCls}>Descrizione (facoltativa)</label>
                <input value={modalSezione.descrizione || ''} onChange={e => setModalSezione({ ...modalSezione, descrizione: e.target.value })} className={inputCls} />
              </div>
              <div className="w-28">
                <label className={labelCls}>Ordine</label>
                <input type="number" value={modalSezione.ordine || 0} onChange={e => setModalSezione({ ...modalSezione, ordine: Number(e.target.value) })} className={inputCls} />
              </div>
            </div>
            <div className="flex justify-between items-center mt-7">
              {modalSezione.id
                ? <button onClick={() => { const s = modalSezione; setModalSezione(null); eliminaSezione(s); }} className="text-sm text-red-600 hover:text-red-700">Elimina</button>
                : <span />}
              <div className="flex gap-2">
                <button onClick={() => setModalSezione(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Annulla</button>
                <button onClick={salvaSezione} disabled={salvando} className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl text-sm font-medium shadow-lg shadow-blue-500/25 hover:shadow-xl disabled:opacity-50 transition-all">{salvando ? '…' : 'Salva'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
